import {
  HydrationBoundary,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import type { PostHogBootstrapData } from '$src/utils/getPostHogBootstrapData';
import type { DehydratedState } from '@tanstack/react-query';
import type { AppProps } from 'next/app';

import { ErrorBoundary } from '$components/ErrorBoundary';
import { ExtensionCartWatcher } from '$src/components/ExtensionCartWatcher';
import { InitWrapper } from '$src/components/InitWrapper';
import { Layout } from '$src/components/Layout';
import { ExtensionProvider } from '$src/context/ExtensionContext';
import { AuthProvider } from '$src/hooks/auth';
import { useSetInitialDistinctId } from '$src/hooks/state/useInitialDistinctId';
import { reportQueryError } from '$src/http';
import { initAnalyticsServices } from '$src/utils';

import '$src/styles/formLoader.scss';
import '$src/styles/globals.scss';
import '@getjust/leaf/dist/leaf.css';
import 'react-phone-number-input/style.css';

import { ServerInjection } from '../components/ServerInjection';
import { StripeProvider } from '../context/StripeContext';

type Props = {
  dehydratedState: DehydratedState;
  cartId: string;
  sellerId: string;
  shopId?: string;
  postHogBootstrapData: PostHogBootstrapData | null;
};


/**
 * FAQ:
 * - Why no jotai (state) provider in the react tree ?
 * cf: https://github.com/pmndrs/jotai/discussions/384
 */

function App({ Component, pageProps }: AppProps<Props>) {
  const setInitialDistinctId = useSetInitialDistinctId();

  // Initialize analytics services synchronously BEFORE the first render
  // This ensures no events are missed during initial render
  const analyticsInitializedRef = useRef(false);
  if (!analyticsInitializedRef.current && typeof window !== 'undefined') {
    setInitialDistinctId(pageProps.postHogBootstrapData?.distinctID);
    initAnalyticsServices(pageProps.postHogBootstrapData);
    analyticsInitializedRef.current = true;
  }

  const [queryClient] = useState(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error: any) => {
            reportQueryError(error);
          },
        }),
        queryCache: new QueryCache({
          onError: (error: any) => {
            reportQueryError(error);
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * (60 * 1000), // 10 mins
            gcTime: 10 * (60 * 1000), // 10 mins
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            retry: false,
            throwOnError: false,
          },
        },
      }),
  );

  return (
    <>
      <Head>
        <title>JUST Checkout</title>
        <meta name="description" content="One click checkout" />

      </Head>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={pageProps.dehydratedState}>
            <ServerInjection shopId={pageProps.shopId} />

            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            <InitWrapper>
              <AuthProvider>
                <ExtensionProvider>
                  <StripeProvider>
                    <ExtensionCartWatcher>
                      <Layout>
                        <Component {...pageProps} />
                      </Layout>
                    </ExtensionCartWatcher>
                  </StripeProvider>
                </ExtensionProvider>
              </AuthProvider>
            </InitWrapper>
          </HydrationBoundary>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}

export default appWithTranslation(App) as React.ComponentType<AppProps<Props>>;
